// =============================================================================
// Script Execution Monitor - 脚本执行监控器
// =============================================================================

import React, { useState, useEffect } from 'react';
import { 
  Play, 
  Square, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Code,
  ChevronDown,
  ChevronRight,
  Eye,
  Download
} from 'lucide-react';

export default function ScriptExecutionMonitor({ 
  terminalId,
  onExecutionSelect,
  compact = false 
}) {
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedExecution, setExpandedExecution] = useState(null);
  const [executionOutput, setExecutionOutput] = useState({});

  useEffect(() => {
    if (terminalId) {
      fetchExecutions();
      // 每5秒刷新一次
      const interval = setInterval(fetchExecutions, 5000);
      return () => clearInterval(interval);
    }
  }, [terminalId]);

  const fetchExecutions = async () => {
    try {
      const response = await fetch(`/api/scripts/terminals/${terminalId}/executions?limit=10`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setExecutions(data.data.executions);
        }
      }
    } catch (error) {
      console.error('Failed to fetch executions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchExecutionOutput = async (executionId) => {
    try {
      const response = await fetch(`/api/scripts/executions/${executionId}/output`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setExecutionOutput(prev => ({
            ...prev,
            [executionId]: data.data.output
          }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch execution output:', error);
    }
  };

  const cancelExecution = async (executionId) => {
    try {
      const response = await fetch(`/api/scripts/executions/${executionId}/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (response.ok) {
        fetchExecutions(); // 刷新列表
      }
    } catch (error) {
      console.error('Failed to cancel execution:', error);
    }
  };

  const downloadOutput = (execution) => {
    const output = executionOutput[execution.id];
    if (!output) return;

    const blob = new Blob([output.fullOutput], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `script-${execution.templateId}-${execution.id}.log`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'running':
        return <Play className="w-4 h-4 text-blue-500 animate-pulse" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'cancelled':
        return <Square className="w-4 h-4 text-gray-500" />;
      default:
        return <Clock className="w-4 h-4 text-yellow-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'completed':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'failed':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'cancelled':
        return 'text-gray-600 bg-gray-50 border-gray-200';
      default:
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    }
  };

  const formatDuration = (ms) => {
    if (!ms) return '-';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) return `${hours}:${(minutes % 60).toString().padStart(2, '0')}:${(seconds % 60).toString().padStart(2, '0')}`;
    if (minutes > 0) return `${minutes}:${(seconds % 60).toString().padStart(2, '0')}`;
    return `${seconds}s`;
  };

  const toggleExpanded = (executionId) => {
    if (expandedExecution === executionId) {
      setExpandedExecution(null);
    } else {
      setExpandedExecution(executionId);
      if (!executionOutput[executionId]) {
        fetchExecutionOutput(executionId);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
        <span className="ml-2 text-sm text-gray-500">加载执行记录...</span>
      </div>
    );
  }

  if (executions.length === 0) {
    return (
      <div className="text-center p-4 text-gray-500 text-sm">
        暂无脚本执行记录
      </div>
    );
  }

  if (compact) {
    return (
      <div className="space-y-2">
        {executions.slice(0, 3).map((execution) => (
          <div
            key={execution.id}
            className="flex items-center space-x-2 p-2 bg-gray-50 rounded text-sm"
          >
            {getStatusIcon(execution.status)}
            <span className="flex-1 truncate">{execution.template?.name || execution.templateId}</span>
            <span className="text-xs text-gray-500">
              {formatDuration(execution.duration)}
            </span>
          </div>
        ))}
        {executions.length > 3 && (
          <div className="text-center">
            <button
              onClick={() => onExecutionSelect && onExecutionSelect(executions)}
              className="text-xs text-blue-600 hover:text-blue-800"
            >
              查看全部 {executions.length} 条记录
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-700">脚本执行记录</h3>
        <button
          onClick={fetchExecutions}
          className="text-xs text-blue-600 hover:text-blue-800"
        >
          刷新
        </button>
      </div>

      <div className="space-y-2">
        {executions.map((execution) => (
          <div
            key={execution.id}
            className="border border-gray-200 rounded-lg overflow-hidden"
          >
            <div className="p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => toggleExpanded(execution.id)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    {expandedExecution === execution.id ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </button>
                  {getStatusIcon(execution.status)}
                  <div>
                    <div className="font-medium text-sm">
                      {execution.template?.name || execution.templateId}
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(execution.startTime).toLocaleString()}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 text-xs font-medium rounded border ${getStatusColor(execution.status)}`}>
                    {execution.status}
                  </span>
                  
                  {execution.status === 'running' && (
                    <button
                      onClick={() => cancelExecution(execution.id)}
                      className="p-1 text-red-600 hover:text-red-800"
                      title="取消执行"
                    >
                      <Square className="w-4 h-4" />
                    </button>
                  )}

                  {(execution.status === 'completed' || execution.status === 'failed') && (
                    <button
                      onClick={() => downloadOutput(execution)}
                      className="p-1 text-gray-600 hover:text-gray-800"
                      title="下载输出"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  )}

                  <button
                    onClick={() => toggleExpanded(execution.id)}
                    className="p-1 text-gray-600 hover:text-gray-800"
                    title="查看详情"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                <span>步骤: {execution.currentStep + 1}/{execution.steps?.length || 0}</span>
                <span>耗时: {formatDuration(execution.duration)}</span>
                {execution.metadata?.expectedDuration && (
                  <span>预计: {formatDuration(execution.metadata.expectedDuration)}</span>
                )}
              </div>

              {execution.status === 'running' && execution.steps && (
                <div className="mt-2">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${((execution.currentStep + 1) / execution.steps.length) * 100}%`
                      }}
                    ></div>
                  </div>
                </div>
              )}
            </div>

            {expandedExecution === execution.id && (
              <div className="border-t border-gray-200 bg-gray-50">
                {executionOutput[execution.id] ? (
                  <div className="p-3">
                    <div className="space-y-3">
                      {executionOutput[execution.id].steps.map((step, index) => (
                        <div key={index} className="border border-gray-200 rounded bg-white">
                          <div className="p-2 bg-gray-100 border-b border-gray-200">
                            <div className="flex items-center space-x-2">
                              {getStatusIcon(step.status)}
                              <span className="text-sm font-medium">
                                步骤 {index + 1}: {step.description}
                              </span>
                              <span className="text-xs text-gray-500">
                                {formatDuration(step.duration)}
                              </span>
                            </div>
                            <div className="text-xs text-gray-600 mt-1 font-mono">
                              {step.command}
                            </div>
                          </div>
                          {step.output && (
                            <div className="p-2">
                              <pre className="text-xs text-gray-700 whitespace-pre-wrap max-h-32 overflow-y-auto">
                                {step.output}
                              </pre>
                            </div>
                          )}
                          {step.error && (
                            <div className="p-2 bg-red-50 border-t border-red-200">
                              <div className="text-xs text-red-700 font-mono">
                                错误: {step.error}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="p-3 text-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mx-auto"></div>
                    <span className="text-xs text-gray-500 mt-1">加载输出...</span>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
